import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { DataTable } from '../../components/ui/DataTable.jsx'
import { Button, EmptyState, Modal, Money, PaymentPill, Qty } from '../../components/ui/index.jsx'
import { useAuth, useCan } from '../../lib/auth.jsx'
import { CAP } from '../../lib/capabilities.js'
import { shopCannotInvoice } from '../../lib/shop.js'
import { formatDate, formatQty, isPositive } from '../../lib/money.js'
import {
  RECENT_SHOWN,
  useAgeing,
  useCustomerCount,
  useDayBook,
  useOutstandingPayable,
  useRecentPurchases,
  useRecentSales,
  useSaleCount,
  useStock,
  useStockAlerts,
  useSupplierTotal,
} from './queries.js'
import RangePicker from './RangePicker.jsx'
import { TODAY, resolve } from './range.js'
import './today.css'

// The landing screen (§11). A curated overview up top, then today's
// documents, then the set-up checklist while there is anything left on it.
//
// Every number here is the server's. Nothing on this screen adds two figures
// together: the day book sends its own SUM() for what was billed and what was
// bought, the ageing report sends the receivable, the payable report sends
// what is owed to suppliers, and stock-on-hand sends what the floor is worth.
// Money arrives as a string and is formatted, never parsed into a total
// (Rule 2). Where a request has not answered, the tile reads as a dash — a
// confident 0.00 for a figure that failed to load is worse than a blank.
//
// Seven numbers, in two rows. The first five are what a shop counts and
// totals — customers, suppliers, sales, purchases, stock — the second two are
// what is still owed, on either side of the ledger. The two owed figures are
// genuinely different kinds of number, on purpose: Customer Outstanding is
// net of advances the shop already holds — the same figure this screen has
// always shown, under "Owed to you" before this pass — and Supplier
// Outstanding is the gross balance across every open purchase, matching the
// Payable column the Suppliers list already prints per row. Making the two
// symmetric here would have meant changing one of those two existing screens
// to match a dashboard tile, rather than the tile matching what each screen
// already meant.
//
// None of the seven names its own period or "as of" — the range picker at the
// top of the page, and the date beside the greeting, are the one place that is
// said. An earlier pass put "today" or "right now" under every label instead,
// which was correct but was also the same sentence seven times on a screen
// meant to be read in five seconds; the picker already says it once, for the
// whole page, and repeating it per tile was the kind of completeness that
// reads as clutter rather than as care.
//
// Role-aware, and the gate is on the request rather than on the markup: a tile
// somebody may not see is a query that is never made, so the screen cannot
// offer a figure the server would refuse. A cashier holds `sell` and
// `view_customers`, so about half the row is theirs; the buying-side figures
// need `purchases` and the cost figure needs `view_cost`, both absent for
// them — not blank, not disabled, and not sent by the server either.
//
// One further scope difference is printed rather than hidden. The day book
// answers a caller without `purchases` with their own entries only, so a
// cashier's "sales today" is theirs — while Recent Sales below is the whole
// shop's, exactly as /invoices is for the same person. The screen says which
// is which instead of quietly mixing them.

export default function TodayPage() {
  const { me } = useAuth()
  const can = useCan()
  const navigate = useNavigate()

  const canSell = can(CAP.SELL)
  const canViewCustomers = can(CAP.VIEW_CUSTOMERS)
  const canViewCost = can(CAP.VIEW_COST)
  const canManageParties = can(CAP.MANAGE_PARTIES)
  const canPurchase = can(CAP.PURCHASES)

  // The range. Today by default, and Today alone sends no dates — see range.js.
  const [preset, setPreset] = useState(TODAY)
  const [custom, setCustom] = useState({ date_from: '', date_to: '' })

  // The anchor every other preset is computed from: the shop's own today, as
  // the server named it. It comes from the FIRST day-book answer and is then
  // held, so switching to "last month" does not lose the one date the rest of
  // the arithmetic depends on.
  const [anchor, setAnchor] = useState(null)

  const dates = resolve(preset, anchor, custom)

  const dayBook = useDayBook(canSell, dates)
  const ageing = useAgeing(canViewCustomers)
  const payable = useOutstandingPayable(canPurchase)
  const stock = useStock(canSell)
  const stockAlerts = useStockAlerts(canSell)

  // The Customers and Suppliers tiles' own counts. Unlike the same-looking
  // calls inside Checklist below, these are not skipped once something else
  // has proven a row exists — the tile prints the real number, so "is there
  // at least one" is not enough. They share a query key with Checklist's own
  // calls, so a shop that needs both never sends the request twice.
  const customerTotal = useCustomerCount(canViewCustomers)
  const supplierTotal = useSupplierTotal(canManageParties)

  // The server's day, taken from its first answer and from nowhere else.
  // Captured once: later answers echo back whatever range was asked for, so
  // reading the anchor off every response would make "last month" reset the
  // anchor to last month and the next preset compute from the wrong day.
  const echoed = dayBook.data?.date_to ?? null
  useEffect(() => {
    if (echoed && !anchor) setAnchor(echoed)
  }, [echoed, anchor])

  const day = anchor ?? echoed
  const bills = useRecentSales(day, canSell, dates)
  const recentPurchases = useRecentPurchases(day, canPurchase, dates)

  const sales = dayBook.data?.sales?.summary
  const purchases = dayBook.data?.purchases?.summary
  const receivables = ageing.data?.summary
  const payables = payable.data?.summary
  const inventory = stock.data?.summary
  const ownScope = dayBook.data?.scope === 'own'

  // The two counts the checklist needs, and usually neither is asked for: one
  // row from the ageing report is itself proof that a bill exists and that a
  // customer exists, because it IS a posted bill made out to one. So a shop
  // with anything outstanding never sends either request.
  //
  // They wait for the ageing answer rather than racing it. Racing would save
  // nothing — on a cold load both would already be in flight before the proof
  // arrived — and a shop whose bills are all settled genuinely needs them, so
  // the shortcut has to be a fact rather than a guess about the common case.
  const ageingUndecided = canViewCustomers && ageing.isPending
  const billsProven = (receivables?.invoices ?? 0) > 0
  const customersProven = (receivables?.customers ?? 0) > 0

  const saleCount = useSaleCount(canSell && !ageingUndecided && !billsProven)
  const customerCount = useCustomerCount(
    canManageParties && canViewCustomers && !ageingUndecided && !customersProven,
  )

  // A tile that failed is a dash, and one line above the row says why. The
  // alternative is seven tiles that each carry their own error message, which
  // on a dropped connection is the same sentence seven times.
  const failed = [dayBook.error, ageing.error, payable.error, stock.error, stockAlerts.error].find(
    Boolean,
  )

  const firstName = me?.full_name?.split(' ')[0] ?? 'there'

  // One modal, one piece of state, for every "i" on this screen. Ten separate
  // popovers would each need their own open/close and their own place to sit
  // relative to a tile the grid keeps resizing; a single shared modal is what a
  // tap on any of them opens, and closing it always leaves the same screen
  // behind.
  const [infoTopic, setInfoTopic] = useState(null)

  return (
    <div className="today">
      {/* No "Dashboard" heading. The nav rail already names the screen, and a
          page whose first line repeats the menu item that got you there spends
          its most valuable line saying nothing. The greeting carries the h1
          instead — one h1 per page, and this is the sentence a person reads. */}
      <header className="today-head">
        <div className="today-title">
          <div className="today-greeting">
            <h1>
              {greeting(new Date().getHours())}, {firstName}
            </h1>
            {/* The date is the server's, formatted. Never the browser's — see
                the note on greeting() for the one thing the browser clock is
                allowed to decide, and why it may be wrong by a greeting and
                nothing else. */}
            <p className="today-when">
              {day ? formatDate(day) : 'Asking the shop what day it is…'}
            </p>
          </div>
          {/* Hard right, on the heading's own line: it governs every figure
              below it, and a control that sits above the tiles instead reads as
              belonging to whichever tile it happens to be nearest. */}
          <RangePicker
            preset={preset}
            dates={dates}
            custom={custom}
            anchor={anchor}
            formatDate={formatDate}
            onPreset={setPreset}
            onCustom={setCustom}
          />
        </div>
      </header>

      {failed ? (
        <p className="today-alert" role="alert">
          {failed.message} Anything that could not be loaded reads as a dash below, not
          as zero.
        </p>
      ) : null}

      {/* ONE SECTION, TWO ROWS, SEVEN NUMBERS.
          The first row is what a shop counts and totals, all five on one
          line (`.today-tiles-5`, a narrower minimum than the ordinary tile
          grid so five fit where four used to); the second is what is still
          owed, on either side of the ledger. Neither row repeats the range
          picker's own answer to "for when" — see the file header. */}
      <section className="today-group" aria-labelledby="today-overview">
        <h2 className="today-group-head" id="today-overview">
          Overview
        </h2>
        <div className="today-tiles-5">
          {canViewCustomers ? (
            <Tile
              label="Customers"
              accent="blue"
              pending={customerTotal.isPending}
              value={formatQty(customerTotal.data?.total ?? 0)}
              to="/customers"
              go="Customers"
            />
          ) : null}

          {canManageParties ? (
            <Tile
              label="Suppliers"
              accent="violet"
              pending={supplierTotal.isPending}
              value={formatQty(supplierTotal.data?.total ?? 0)}
              to="/suppliers"
              go="Suppliers"
            />
          ) : null}

          {canSell ? (
            <Tile
              label={ownScope ? 'You billed' : 'Total Sales'}
              accent="green"
              pending={dayBook.isPending}
              value={<Money value={sales?.total_amount} />}
              note={billedNote(sales)}
              to="/reports/day-book"
              go="Day book"
              onInfo={() => setInfoTopic('sales')}
            />
          ) : null}

          {canPurchase ? (
            <Tile
              label="Total Purchases"
              accent="amber"
              pending={dayBook.isPending}
              value={<Money value={purchases?.total_amount} />}
              note={count(purchases?.entry_count ?? 0, 'purchase')}
              to="/reports/day-book"
              go="Day book"
              onInfo={() => setInfoTopic('purchases')}
            />
          ) : null}

          {canSell && canViewCost ? (
            <Tile
              label="Stock Value"
              pending={stock.isPending}
              value={<Money value={inventory?.total_stock_value} />}
              note={count(inventory?.products ?? 0, 'product') + ' on the floor'}
              to="/reports/stock-on-hand"
              go="Stock on hand"
              onInfo={() => setInfoTopic('stock_cost')}
            />
          ) : null}
        </div>

        {/* The owed row: what the shop is chasing, and what it still has to
            pay out. Not netted against each other and not against their own
            advances the same way — see the file header for why the two stay
            asymmetric on purpose. */}
        {canPurchase || canViewCustomers ? (
          <div className="today-tiles">
            {canPurchase ? (
              <Tile
                label="Supplier Outstanding (Payable)"
                pending={payable.isPending}
                value={
                  <Money
                    value={payables?.total_payable}
                    tone={isPositive(payables?.total_payable) ? 'due' : 'none'}
                  />
                }
                note={count(payables?.purchases ?? 0, 'purchase') + ' pending'}
                to="/suppliers"
                go="Suppliers"
                onInfo={() => setInfoTopic('supplier_payable')}
              />
            ) : null}

            {canViewCustomers ? (
              <Tile
                // NET receivable, not the gross outstanding: the server
                // subtracts the advances the shop is already holding. Chasing
                // a customer for money sitting in your own till is the
                // collections call nobody wants to make, and the gross figure
                // is what causes it. The gross is on /outstanding, one click
                // away, where the buckets that add up to it also live.
                //
                // tone="credit" here, not "due" — a deliberate exception to
                // how every other balance on this page is coloured. Everywhere
                // else, red marks a single document that is still unpaid.
                // Here the two tiles are being read against each other as the
                // two sides of the ledger: what is coming to the shop against
                // what the shop still has to pay out — an asset and a
                // liability, not two unpaid bills. Colouring both red taught
                // the eye nothing; colouring them oppositely is what a
                // shopkeeper actually wants at a glance. The Selling workflow
                // card below carries the same figure and is coloured to match.
                label="Customer Outstanding (Receivable)"
                pending={ageing.isPending}
                value={
                  <Money
                    value={receivables?.to_collect?.amount ?? receivables?.net_receivable}
                    tone={isPositive(receivables?.to_collect?.amount ?? receivables?.net_receivable) ? 'credit' : 'none'}
                  />
                }
                note={count(receivables?.invoices ?? 0, 'invoice') + ' pending'}
                to="/outstanding"
                go="Collections"
                onInfo={() => setInfoTopic('owed')}
              />
            ) : null}
          </div>
        ) : null}
      </section>

      {ownScope ? (
        <p className="today-scope">
          Those two figures are your own entries. Recent Sales below is the whole shop's.
        </p>
      ) : null}

      {/* Ahead of the two tables and the checklist used to sit behind it: what
          you may still be setting up matters more than a list you have already
          seen today, and it should not require scrolling past a table of
          documents to find. */}
      <Checklist
        me={me}
        can={can}
        stock={stock}
        billsProven={billsProven}
        customersProven={customersProven}
        saleCount={saleCount}
        customerCount={customerCount}
      />

      {/* Side by side on a screen wide enough for both — the two sides of the
          same ledger, read the same way. Each is its own <section>, so a
          screen reader and a narrow phone both get two named tables rather
          than one grid whose second half is unannounced. */}
      {canSell || canPurchase ? (
        <div className="today-recent">
          {canSell ? (
            <RecentSales
              day={day}
              query={bills}
              // Only when the count has come back and said so. "No sales
              // yet" is the onboarding sentence and it is wrong for a shop
              // with four hundred of them — and today's sales can land
              // before the count that would have corrected it, so the doubt
              // is resolved the safe way.
              neverBilled={!billsProven && saleCount.data ? saleCount.data.total === 0 : false}
              onStart={() => navigate('/billing')}
            />
          ) : null}

          {canPurchase ? (
            <RecentPurchases day={day} query={recentPurchases} onStart={() => navigate('/purchases/new')} />
          ) : null}
        </div>
      ) : null}

      {canSell ? <StockAlerts query={stockAlerts} /> : null}

      <Workflows
        canPurchase={canPurchase}
        canSell={canSell}
        canManageParties={canManageParties}
        purchases={purchases}
        inventory={inventory}
        sales={sales}
        receivables={receivables}
        onInfo={setInfoTopic}
      />

      <InfoModal topic={infoTopic} onClose={() => setInfoTopic(null)} />
    </div>
  )
}

// ── the "i" on every tile and workflow ──────────────────────────────────────
//
// The dashboard's own numbers, in the shop's own words. Every entry pairs a
// plain-language answer to "what is this" with a worked example, because a
// definition alone still leaves "so what do I do about it" unanswered — and
// where a figure and a workflow are two views of the same money, `related`
// names the other one so the two are never read as unconnected facts.
//
// Static, module-level data: nothing here depends on a render, and keeping it
// outside the component means opening the modal never re-creates the object
// React is about to diff against itself.
const INFO_CONTENT = {
  buying: {
    kicker: 'Workflow',
    title: 'Buying — bringing stock in',
    body: 'Three steps, and the middle one does double duty: recording what you bought is also how the tile itself gets added to your product list and its stock goes up. You never fill in a product form and a purchase form separately for the same tiles. Once a supplier exists, open their own page (Suppliers) — every step below is a button right there, already knowing who it is for.',
    steps: [
      'Add a supplier — once, the first time you buy from them. "Add a supplier" here does it with the full form (GST state, credit terms and all); typing their name straight into a purchase and picking "Add […] as a supplier" does the same thing with just a name, mid-purchase. Either way, saving takes you straight into a short setup screen — record a purchase (or bulk-import their price list), then make a payment, both skippable — before it hands you off to their permanent page.',
      'Record the purchase when it arrives: type in the tile, the quantity, the rate — or photograph their invoice and let it read those for you to check. GST and the total show as you go, so you can pay the right amount on the same screen. A tile you have never stocked? "Add new product" puts it on the purchase and in your products at once — or bulk-import their whole price list. Both ways are buttons on the supplier’s own page too — "Type in a purchase" and "Upload a purchase" — for every purchase after the first.',
      'Make a payment — now, in full, or later, in part. "Supplier payment" on their page for money sent ahead of a purchase; take payment on the purchase itself in Purchases to settle that one.',
      'Running low on something, or a customer asked for a tile you don’t stock? "Request a product" here logs it with a deadline — a restock or something brand new, either way. It sits on the Product requests list until a bill you tick it against closes it; nothing is ever matched or closed on its own.',
    ],
    example: 'Kajaria delivers 200 boxes of 600×600 tiles for ₹85,000. You add Kajaria as a supplier the first time. Every purchase after that, open Kajaria’s own page and record or upload what’s on it — the tax and the stock work themselves out.',
    related: 'Shows up in: Total Purchases, Stock Value and Supplier Outstanding on this screen, and Product requests.',
  },
  selling: {
    kicker: 'Workflow',
    title: 'Selling — billing a customer',
    body: 'The same shape as Buying, mirrored: someone to bill, what you’re billing them, and what they’ve paid. Once a named customer exists, open their own page (Customers) — billing them, uploading their order and taking a payment are all buttons right there.',
    steps: [
      'Add a customer, or use Walk-in for a counter sale. "Add a customer" here opens the full form; typing their name straight into a bill and picking "Add […] as a customer" does the same thing with just a name, mid-bill. Saving from the full form takes you into a short setup screen next — create a bill, receive payment, both skippable — before it hands you off to their permanent page.',
      'Search the tile and add it to the bill — tax and totals work themselves out. Got their order or quotation on paper instead? Upload it and check what it read rather than typing each line. Both — "Bill them" and "Upload an order" — are buttons on the customer’s own page too, for every order after the first.',
      'Print the invoice, and take payment now or record it as owed. "Receive payment" on their page is for money taken ahead of a bill; take payment on the bill itself in Invoices to settle that one.',
    ],
    example: 'Mr. Reddy buys 40 boxes for his living room and wants a week to pay. Bill him against his own name instead of Walk-in, and it shows up under “Owed to you” until he pays — from then on, his own page is where you go for his next bill too.',
    related: 'Shows up in: Total Sales and Customer Outstanding on this screen.',
  },
  sales: {
    kicker: 'In this period',
    title: 'Total Sales',
    body: 'The total value of everything billed to customers in the period you’ve chosen — whether or not you’ve been paid for it yet.',
    example: 'You bill ₹85,000 today. It counts here today, even if the customer takes a week to pay.',
    related: 'Part of the Selling workflow, below.',
  },
  purchases: {
    kicker: 'In this period',
    title: 'Total Purchases',
    body: 'What you’ve bought from suppliers in the period you’ve chosen, whether or not you’ve paid them yet.',
    example: 'Kajaria’s ₹85,000 purchase counts the day you record it — not the day you pay it.',
    related: 'Part of the Buying workflow, below.',
  },
  owed: {
    kicker: 'Right now',
    title: 'Customer Outstanding (Receivable)',
    body: 'Every rupee customers still owe you, across every unpaid bill, added together — already reduced by any advance they’ve paid you. Oldest bills count first.',
    example: 'If ₹10 lakh of a much larger total has been outstanding for more than 90 days, that is the group worth calling first — the exact split sits on Outstanding, one click away.',
    related: 'Part of the Selling workflow, below.',
  },
  supplier_payable: {
    kicker: 'Right now',
    title: 'Supplier Outstanding (Payable)',
    body: 'What you still owe suppliers, across every posted purchase with a balance left on it — the same figure the Suppliers list shows per row, added together. Unlike Customer Outstanding, this one is not reduced by any on-account advance you’ve already paid a supplier: it is the gross balance sitting against open bills.',
    example: 'Kajaria’s ₹85,000 purchase counts here until it is paid off, in full or in part — same as it does on Kajaria’s own page.',
    related: 'Part of the Buying workflow, below.',
  },
  stock_cost: {
    kicker: 'Right now',
    title: 'Stock Value',
    body: 'What’s sitting in your godown right now, valued at what you paid for it — not what you’ll sell it for.',
    example: 'If your floor holds 56 products worth ₹39 lakh at cost, that is what it would take to buy all of it again today.',
    related: 'Fed by the Buying workflow, below — every purchase adds to it.',
  },
}

function InfoModal({ topic, onClose }) {
  const info = topic ? INFO_CONTENT[topic] : null
  return (
    <Modal open={Boolean(info)} onClose={onClose} title={info?.title ?? ''}>
      {info ? (
        <div className="today-info">
          <p className="today-info-kicker">{info.kicker}</p>
          <p>{info.body}</p>
          {info.steps ? (
            <ol className="today-info-steps">
              {info.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          ) : null}
          {info.example ? (
            <div className="today-info-example">
              <span className="today-info-example-label">Example</span>
              {info.example}
            </div>
          ) : null}
          {info.related ? <p className="today-info-related">{info.related}</p> : null}
        </div>
      ) : null}
    </Modal>
  )
}

// ── the two workflows ────────────────────────────────────────────────────────

/**
 * A button per document, not a tile per figure: this is where a shop that
 * still has to think "how do I record what I bought?" is answered rather than
 * shown a number. Record/Bill and Upload both open on a form that can create
 * everything it needs mid-flow — search for a supplier, a tile or a customer
 * that does not exist yet and a quick-add opens right there, not a trip away
 * from the bill and back.
 *
 * "Add a supplier" / "Add a customer" is offered anyway, quietly, alongside
 * them: a brand-new relationship is the one case where somebody would rather
 * fill in the whole form once — GST state, credit terms, the lot — than have
 * it opened for them mid-bill with only a name to go on. Once they exist,
 * their own page (this button's real destination for everything after the
 * first save) is where the rest of the workflow lives.
 *
 * Last on the screen rather than first: the figures above are what a shop
 * checks twenty times a day, and a card teaching it what a button does is not
 * worth the space above them every time.
 */
function Workflows({
  canPurchase, canSell, canManageParties, purchases, inventory, sales, receivables, onInfo,
}) {
  if (!canPurchase && !canSell) return null

  return (
    <section className="today-group" aria-labelledby="today-workflows">
      <h2 className="today-group-head" id="today-workflows">Your workflows</h2>
      <div className="today-workflows">
        {canPurchase ? (
          <div className="today-workflow">
            <div className="today-workflow-head">
              <span className="today-workflow-label">Buying</span>
              {onInfo ? (
                <button
                  type="button"
                  className="today-tile-info"
                  aria-label="How the Buying workflow works"
                  onClick={() => onInfo('buying')}
                >
                  i
                </button>
              ) : null}
            </div>
            <p className="today-workflow-live">
              {count(inventory?.products ?? 0, 'product')} in stock ·{' '}
              {count(purchases?.entry_count ?? 0, 'purchase')} today
            </p>
            <div className="today-workflow-ctas">
              <Link
                className="ui-btn ui-btn-primary ui-btn-sm today-workflow-cta"
                to="/purchases/new"
              >
                Record a purchase
              </Link>
              <Link
                className="ui-btn ui-btn-secondary ui-btn-sm today-workflow-cta"
                to="/purchases/upload"
              >
                Upload a purchase
              </Link>
              {canManageParties ? (
                <Link
                  className="ui-btn ui-btn-quiet ui-btn-sm today-workflow-cta"
                  to="/suppliers/new"
                >
                  Add a supplier
                </Link>
              ) : null}
              <Link
                className="ui-btn ui-btn-quiet ui-btn-sm today-workflow-cta"
                to="/product-requests/new"
              >
                Request a product
              </Link>
            </div>
          </div>
        ) : null}

        {canSell ? (
          <div className="today-workflow">
            <div className="today-workflow-head">
              <span className="today-workflow-label">Selling</span>
              {onInfo ? (
                <button
                  type="button"
                  className="today-tile-info"
                  aria-label="How the Selling workflow works"
                  onClick={() => onInfo('selling')}
                >
                  i
                </button>
              ) : null}
            </div>
            <p className="today-workflow-live">
              {/* Same figure, same "credit" tone as the Customer Outstanding
                  tile above — see the note there on why receivable is
                  coloured as an asset here rather than as an unpaid bill. */}
              <Money
                value={receivables?.to_collect?.amount ?? receivables?.net_receivable}
                tone={isPositive(receivables?.to_collect?.amount ?? receivables?.net_receivable) ? 'credit' : 'none'}
              />{' '}
              owed to you · {count(sales?.entry_count ?? 0, 'bill')} today
            </p>
            <div className="today-workflow-ctas">
              <Link className="ui-btn ui-btn-primary ui-btn-sm today-workflow-cta" to="/billing">
                Bill a customer
              </Link>
              <Link
                className="ui-btn ui-btn-secondary ui-btn-sm today-workflow-cta"
                to="/billing/upload"
              >
                Upload an order
              </Link>
              {canManageParties ? (
                <Link
                  className="ui-btn ui-btn-quiet ui-btn-sm today-workflow-cta"
                  to="/customers/new"
                >
                  Add a customer
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </section>
  )
}

// ── the tiles ────────────────────────────────────────────────────────────────

/**
 * One figure, and the screen it came from.
 *
 * The whole tile is the link, and the destination is named in accent ink at the
 * foot of it: if it is clickable it is purple (§09). The figure itself stays in
 * `--ink` by default, because a number that is also a link colour reads as a
 * state.
 *
 * `accent` is the one deliberate exception, and only on the five Overview
 * tiles: a `--stat-*` token (never `--accent`, never a payment-state token —
 * see tokens.css) that tells Customers, Suppliers, Total Sales and Total
 * Purchases apart at a glance, the way the reference dashboard does. Every
 * other tile on the page — Stock Value included — leaves it unset and stays
 * plain ink, same as before.
 *
 * No per-tile "as of" or period label — the range picker at the top of the
 * page is the one place that is said, for every tile at once. A tile whose
 * value is a POSITION (what is owed, what the floor is worth) rather than a
 * sum over the chosen range still does not follow the picker, but that is
 * now trusted to the picker's own caption rather than restated seven times.
 */
function Tile({ label, value, note, pending, to, go, onInfo, accent }) {
  return (
    // The wrapper exists for one reason: the "i" sits on top of a tile that is
    // itself a whole <Link>, and a <button> nested inside an <a> is invalid
    // HTML and cannot be told apart from the link at the accessibility layer
    // either. As a sibling instead, positioned over the same corner, a tap on
    // it never reaches the link beneath — there is no DOM relationship for the
    // click to bubble through.
    <div className="today-tile-wrap">
      <Link className="today-tile" to={to}>
        <span className="today-tile-label">{label}</span>
        {/* A skeleton, not a dash, while the request is in flight. A dash means
            "there is nothing here", and on first paint that is not yet known. */}
        <span className={accent ? `today-tile-value today-tile-value--${accent}` : 'today-tile-value'}>
          {pending ? <span className="ui-skel" /> : value}
        </span>
        {note ? <span className="today-tile-note">{note}</span> : null}
        {/* Its own line, not appended to the note. Inline, the separator and the
            destination broke across a line of their own on the narrow tiles —
            "Nothing collected yet ·" and then the link underneath it. */}
        <span className="today-tile-go">{go} →</span>
      </Link>
      {onInfo ? (
        <button
          type="button"
          className="today-tile-info"
          aria-label={`What ${label} means`}
          onClick={onInfo}
        >
          i
        </button>
      ) : null}
    </div>
  )
}

/** "4 bills", "1 bill", "0 bills". Counting words, never a money string — the
 *  server sends every amount already summed, and there is no arithmetic here. */
function count(n, word) {
  const value = n ?? 0
  return `${formatQty(value)} ${value === 1 ? word : `${word}s`}`
}

function billedNote(summary) {
  if (!summary) return null
  const billed = summary.entry_count ? count(summary.entry_count, 'bill') : 'Nothing billed yet'
  // Cancelled bills are listed in the day book and excluded from every total
  // there, so the count beside the money has to say so or the two disagree.
  if (!summary.cancelled_count) return billed
  return `${billed} · ${count(summary.cancelled_count, 'cancellation')}`
}

/**
 * Which half of the day it is, from the browser clock — the one thing on this
 * screen that comes from it.
 *
 * It looks like the rule about dates and is not one. Every date and every
 * figure here is derived AT TIME ZONE 'Asia/Kolkata' in SQL, because a browser
 * that decides "today" for itself files a 31 March evening's bills in the wrong
 * financial year. This is an adjective addressed to the person in front of the
 * screen: it is stored nowhere, nothing is derived from it, and it is at worst
 * wrong by a greeting. The hardcoded "Good morning" it replaces was wrong by a
 * greeting for the entire evening shift, every day.
 */
export function greeting(hour) {
  // The small hours belong to the evening that has not ended yet. A shop
  // cashing up at half past midnight is finishing Tuesday, not starting
  // Wednesday, and "Good morning" at 00:21 reads as a clock that is broken
  // rather than a greeting that is generous.
  if (hour < 5) return 'Good evening'
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

// ── recent sales ─────────────────────────────────────────────────────────────

const STATE_LABELS = {
  paid: 'Paid',
  partial: 'Partial',
  unpaid: 'Unpaid',
  overdue: 'Overdue',
}

/**
 * The status cell, and why it is not <PaymentPill> — the component the
 * purchases table below uses.
 *
 * <PaymentPill> derives the state from received/total/due/today, and the today
 * it would need is the browser's. Every sale row the server sends already
 * carries `payment_state`, derived by vocab.payment_state() against the
 * database's date. This renders that answer and adds nothing to it — the same
 * argument, and the same three ui.css classes, as invoices/StatePill.jsx and
 * PaymentPill itself, so the two tables' badges read as one system in spite
 * of coming from two different derivations.
 */
function BillState({ state }) {
  const safe = STATE_LABELS[state] ? state : 'unpaid'
  return <span className={`ui-pill ui-pill-${safe}`}>{STATE_LABELS[safe]}</span>
}

/**
 * Five columns, not the Invoices screen's own eight: Ref, Party, Date,
 * Amount, Status. Two tables now share one row, each holding roughly half
 * the width the earlier six-column version (Bill+sub, Customer+sub, Total,
 * Balance, Status, Actions) was sized for — that version was correct in
 * isolation and overflowed the moment it had a neighbour, forcing a
 * horizontal scrollbar inside a card meant to be read at a glance. Balance
 * and Actions are gone rather than shrunk: the status pill already answers
 * "is this settled", the exact balance and the Take-payment button are one
 * click away on Invoices, and no sub-line (who wrote it, their phone) makes
 * the same trade a second time. The invoice number is coloured, not linked
 * — see .today-ref-sale — so it stays plain data rather than competing with
 * the page's own rule that purple is the only colour that means "click me".
 */
const BILL_COLUMNS = [
  {
    key: 'invoice_number',
    header: 'Ref',
    width: '150px',
    priority: 1,
    cell: (r) => <span className="mono today-ref today-ref-sale">{r.invoice_number}</span>,
  },
  {
    key: 'customer_name',
    header: 'Party',
    width: 'minmax(90px, 1fr)',
    priority: 1,
    // The document's own snapshot, not the master (Rule 5). Truncated to one
    // line rather than wrapped: "Kajaria Ceramics Ltd — Hyderabad Depot" at
    // this column width would otherwise be a three-line-tall row next to a
    // one-line neighbour, and the full name is one click away regardless.
    cell: (r) => <span className="today-party">{r.customer_name}</span>,
  },
  {
    key: 'invoice_date',
    header: 'Date',
    width: '96px',
    priority: 2,
    cell: (r) => <span className="today-date">{formatDate(r.invoice_date)}</span>,
  },
  {
    key: 'total_amount',
    header: 'Amount',
    width: '96px',
    align: 'right',
    priority: 1,
    cell: (r) => <Money value={r.total_amount} />,
  },
  {
    key: 'payment_state',
    header: 'Status',
    width: '88px',
    priority: 1,
    // A cancelled bill is a document state, not a payment state. A dashed
    // neutral tag says "this is not a live document" without teaching the eye
    // that two unrelated facts are the same fact.
    cell: (r) =>
      r.status === 'cancelled' ? (
        <span className="today-tag">Cancelled</span>
      ) : (
        <BillState state={r.payment_state} />
      ),
  },
]

function RecentSales({ day, query, neverBilled, onStart }) {
  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0

  return (
    <section className="today-bills" aria-label="Recent sales">
      <div className="today-section-head">
        <h2>Recent Sales</h2>
        {day ? (
          // The full day, on the screen built for reading a list of bills. The
          // dates go in the query string the way that screen's own filters do,
          // so the link reproduces the view rather than describing it.
          <Link className="today-more" to={`/invoices?from=${day}&to=${day}`}>
            Open the day →
          </Link>
        ) : null}
      </div>

      {total > rows.length ? (
        <p className="today-note">
          The {formatQty(RECENT_SHOWN)} most recent of {count(total, 'sale')}.
        </p>
      ) : null}

      <DataTable
        caption="Recent sales"
        columns={BILL_COLUMNS}
        rows={rows}
        rowKey="id"
        // Pending covers the wait for the day book as well as for this request:
        // the query is disabled until the server has said what day it is, and
        // skeleton rows are the right thing on screen for both halves of that.
        loading={query.isPending}
        error={query.error ? query.error.message : null}
        empty={
          <EmptyState
            title={
              neverBilled
                ? 'No bills yet. Post your first one at the counter and it appears here, ready to print.'
                : 'Nothing billed yet in this range. The next bill you post appears here.'
            }
            action={
              <Button variant="primary" onClick={onStart}>
                Start a bill
              </Button>
            }
          />
        }
      />
    </section>
  )
}

// ── recent purchases ─────────────────────────────────────────────────────────

/**
 * A function of `day`, not a module-level constant like BILL_COLUMNS above
 * it — the status column's PaymentPill needs the server's day, and that
 * lives on the component, not at module scope. The bug this replaced:
 * PURCHASE_COLUMNS as a plain array referenced `day` in a closure over
 * nothing, which threw ReferenceError the first time a row actually reached
 * that cell — invisible on an empty table (the cell function is never
 * called), so it shipped clean and broke the moment a range with real
 * purchases in it was picked.
 */
/**
 * Same five-column shape as BILL_COLUMNS above, for the same reason — see
 * its own doc comment. Ref, Party, Date, Amount, Status; no Balance, no
 * Actions, no sub-lines, the purchase number coloured rather than linked
 * (.today-ref-purchase).
 */
function purchaseColumns(day) {
  return [
  {
    key: 'purchase_number',
    header: 'Ref',
    width: '150px',
    priority: 1,
    cell: (r) => <span className="mono today-ref today-ref-purchase">{r.purchase_number}</span>,
  },
  {
    key: 'supplier_name',
    header: 'Party',
    width: 'minmax(90px, 1fr)',
    priority: 1,
    // The document's own snapshot, not the master (Rule 5) — same as the
    // customer column opposite it, and truncated the same way.
    cell: (r) => <span className="today-party">{r.supplier_name}</span>,
  },
  {
    key: 'purchase_date',
    header: 'Date',
    width: '96px',
    priority: 2,
    cell: (r) => <span className="today-date">{formatDate(r.purchase_date)}</span>,
  },
  {
    key: 'total_amount',
    header: 'Amount',
    width: '96px',
    align: 'right',
    priority: 1,
    cell: (r) => <Money value={r.total_amount} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: '88px',
    priority: 1,
    cell: (r) =>
      r.status === 'cancelled' ? (
        <span className="today-tag">Cancelled</span>
      ) : (
        // A purchase carries no payment_state column the way a sale does, so
        // the badge is derived client-side — the exact call the Purchases
        // list itself makes (PurchasesPage.jsx) — but `today` here is the
        // server's own day, echoed by the day book, rather than that
        // screen's istToday(): this table already has the authoritative
        // date in scope and has no reason to ask the browser for a second
        // opinion.
        <PaymentPill received={r.amount_paid} total={r.total_amount} dueDate={r.due_date} today={day} />
      ),
  },
  ]
}

function RecentPurchases({ day, query, onStart }) {
  const rows = query.data?.rows ?? []
  const total = query.data?.total ?? 0
  const columns = purchaseColumns(day)

  return (
    <section className="today-bills" aria-label="Recent purchases">
      <div className="today-section-head">
        <h2>Recent Purchases</h2>
        {day ? (
          <Link className="today-more" to={`/purchases?from=${day}&to=${day}`}>
            Open the day →
          </Link>
        ) : null}
      </div>

      {total > rows.length ? (
        <p className="today-note">
          The {formatQty(RECENT_SHOWN)} most recent of {count(total, 'purchase')}.
        </p>
      ) : null}

      <DataTable
        caption="Recent purchases"
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={query.isPending}
        error={query.error ? query.error.message : null}
        empty={
          <EmptyState
            title="Nothing purchased yet in this range. The next purchase you record appears here."
            action={
              <Button variant="primary" onClick={onStart}>
                Record a purchase
              </Button>
            }
          />
        }
      />
    </section>
  )
}

// ── stock alerts ─────────────────────────────────────────────────────────────

/**
 * The reference dashboard's own bottom strip: which products need buying
 * before they need explaining, not just how many. Waits for a settled
 * answer before rendering anything — a request still in flight has no
 * verdict to show yet, and an error is already covered by the banner above
 * everything else on the page — but SETTLED AND CLEAR IS STILL SHOWN, with
 * the same reassurance the old Running Low tile gave: "every product is
 * above its reorder level" reads as a verified fact, not as a section that
 * quietly failed to appear. A shop with nothing to alert on today is exactly
 * the shop this section exists to keep it that way for.
 *
 * Out of stock and low stock are read straight off the summary
 * (`out_of_stock_products`, `low_stock_products` minus that same subset),
 * never counted from the four cards on screen — the strip is capped at
 * four, and a fifth alert would otherwise vanish from the badge along with
 * the card.
 */
function StockAlerts({ query }) {
  if (query.isPending || query.error) return null

  const summary = query.data?.summary
  const rows = query.data?.rows ?? []
  const outOfStock = summary?.out_of_stock_products ?? 0
  const lowOnly = Math.max(0, (summary?.low_stock_products ?? 0) - outOfStock)
  const clear = outOfStock === 0 && lowOnly === 0

  return (
    <section className="today-alerts" aria-label="Stock alerts">
      <div className="today-section-head">
        <h2>
          Stock Alerts
          {clear ? null : (
            <span className="today-alert-badges">
              {outOfStock > 0 ? (
                <span className="today-alert-badge is-out">{formatQty(outOfStock)} out of stock</span>
              ) : null}
              {lowOnly > 0 ? (
                <span className="today-alert-badge is-low">{formatQty(lowOnly)} low stock</span>
              ) : null}
            </span>
          )}
        </h2>
        <Link className="today-more" to="/reports/stock-on-hand">
          Stock on hand →
        </Link>
      </div>
      {clear ? (
        <p className="today-note">Every product is above its reorder level.</p>
      ) : (
        <div className="today-alert-cards">
          {rows.map((r) => (
            <Link
              key={r.id}
              to={`/products/${r.id}`}
              // A product exactly at zero is "out"; anything above zero and
              // at or below its own reorder level (the predicate that put
              // it on this list at all) is "low" — the same split the two
              // badges above already made off the summary.
              className={isPositive(r.current_stock) ? 'today-alert-card is-low' : 'today-alert-card is-out'}
            >
              <span className="today-alert-name">{r.name}</span>
              <span className="today-alert-detail">
                {/* Same convention as the Stock on hand report's own "On
                    hand" column: boxes for a boxed tile, else the base unit,
                    both said by the server — no packaging maths in JavaScript. */}
                {r.stock_in_boxes ?? <Qty value={r.current_stock} unit={r.base_unit} />}
                {r.size ? ` · ${r.size}` : null}
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

// ── the set-up checklist ─────────────────────────────────────────────────────

/**
 * The first ten minutes, and nothing after them.
 *
 * Every step's done-ness is derived from a real count, never assumed and never
 * hardcoded: a checklist that ticks a box the shop has not done teaches them to
 * ignore it, and this is the whole of onboarding.
 *
 * `done` is a tri-state on purpose. `null` is "not known yet", which is not
 * "not done" — a shop with four hundred bills must not watch five empty boxes
 * paint and then vanish half a second later on the screen it opens most. So
 * nothing renders until every visible step has an answer, and once they are all
 * yes the section is gone rather than sitting there fully ticked forever.
 */
function Checklist({ me, can, stock, billsProven, customersProven, saleCount, customerCount }) {
  const inventory = stock.data?.summary

  const steps = [
    {
      label: 'Business details and GSTIN',
      hint: 'Your GSTIN and address print on every invoice. Without them the bill is not a tax invoice, and no bill can be posted.',
      to: '/settings',
      // /settings is gated on CAP.SETTINGS. Offering a cashier a step that
      // answers "you do not have access to this" is worse than not offering
      // it — hide what the caller may not do, never disable it.
      show: can(CAP.SETTINGS),
      // The server's own verdict, not `Boolean(gstin)`: it is the same
      // `invoice_ready` that decides whether POST /api/sales is refused, and a
      // checklist that ticks while the till still refuses is a checklist
      // nobody trusts again.
      done: me?.tenant ? !shopCannotInvoice(me.tenant) : null,
    },
    {
      label: 'Add your first tile',
      hint: 'Size, HSN, GST rate and how it is sold — by the box, the piece or the square foot.',
      to: '/products',
      show: can(CAP.MANAGE_PRODUCTS),
      done: stock.data ? (inventory?.products ?? 0) > 0 : null,
    },
    {
      label: 'Set opening stock',
      // The single most-stocked product answers this for the whole master: if
      // the largest holding is zero, nothing has been counted in.
      hint: 'What is on the floor today, so a bill can take stock off it.',
      to: '/products',
      show: can(CAP.SET_OPENING_STOCK),
      done: stock.data ? isPositive(stock.data.rows?.[0]?.current_stock) : null,
    },
    {
      label: 'Add a customer',
      hint: 'Their GSTIN and credit terms, so a bill knows when it falls due.',
      to: '/customers/new',
      show: can(CAP.MANAGE_PARTIES),
      done: customersProven ? true : known(customerCount),
    },
    {
      label: 'Write your first bill',
      hint: 'Stock comes off, the invoice number is allocated, and it prints.',
      to: '/billing',
      show: can(CAP.SELL),
      done: billsProven ? true : known(saleCount),
    },
  ]

  const visible = steps.filter((step) => step.show)
  const settled = visible.every((step) => step.done !== null)
  const done = visible.filter((step) => step.done === true).length
  if (!settled || done === visible.length) return null

  return (
    <section className="today-checklist" aria-label="Set-up checklist">
      <div className="today-section-head">
        <h2>Finish setting up</h2>
        <p className="today-progress">
          {formatQty(done)} of {formatQty(visible.length)} done
        </p>
      </div>
      <ol className="today-steps">
        {visible.map((step) => (
          <li key={step.label} className={step.done ? 'today-step is-done' : 'today-step'}>
            <span className="today-step-mark" aria-hidden="true">
              {step.done ? '✓' : ''}
            </span>
            <span className="today-step-body">
              {/* <Link>, not <a href>. A bare anchor here left the router
                  entirely: the browser threw the running application away,
                  re-booted React and asked /auth/me again, on the one screen a
                  shop reaches first. It was invisible while the whole app was a
                  single cached bundle and is not now that each screen is its own
                  chunk. */}
              <Link to={step.to}>{step.label}</Link>
              <span className="today-step-hint">{step.hint}</span>
            </span>
            <span className="visually-hidden">{step.done ? 'Done' : 'Not done'}</span>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** A §07a list answer turned into yes / no / not yet known. `total` is the
 *  server's count over the same predicate as the row it did not need to send. */
function known(query) {
  if (!query.data) return null
  return (query.data.total ?? 0) > 0
}
